import { useLazyQuery } from '@apollo/client';
import { LoadingButton } from '@atlaskit/button';
import { CopyableText, DynamicResultTable } from '@css-xp/base';
import {
  BillingAdmin,
  FetchTransactionAccountSearchQuery,
  FetchTransactionAccountSearchQueryVariables,
  TransactionAccount,
} from '@css-xp/graphtypes/src/generated-types-new';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE, NO_DETAILS_TO_SHOW } from '@atlassian/cssxp-utils-entitlement';
import { defaultTo } from 'lodash';
import React, { useState } from 'react';
import { queryTransactionAccount } from '../queries/transaction-account-search';
import compact from 'lodash/compact';
import capitalize from 'lodash/capitalize';
import { FormData } from '../subscription-txa-transfer-pane';

type Props = {
  formState: FormData;
  transactionAccountNumber: String | undefined | null;
  selectBillingAccount: (billingAccount: TransactionAccount) => void;
};

const defaultSearchOptions = {
  first: DEFAULT_ROWS_PER_PAGE,
  offset: 0,
};

export const emptyStateOrderProp = {
  header: '',
  description: NO_DETAILS_TO_SHOW,
  imageUrl: '',
};

export const BillingAccount = ({ billingAccount }: { billingAccount: TransactionAccount }) => {
  return (
    <>
      <div>{defaultTo(billingAccount?.displayName, 'Billing account')}</div>
      <CopyableText text={billingAccount?.transactionAccountNumber} />
    </>
  );
};

export const AddressCell = ({ billingAccount }: { billingAccount: TransactionAccount }) => {
  const address: string[] = [];
  address.push(defaultTo(billingAccount?.billToParty?.addressLine1, ''));
  address.push(defaultTo(billingAccount?.billToParty?.addressLine2, ''));
  address.push(defaultTo(billingAccount?.billToParty?.city, ''));
  address.push(defaultTo(billingAccount?.billToParty?.state + ' ' + billingAccount?.billToParty?.postcode, ''));
  address.push(defaultTo(billingAccount?.billToParty?.country, ''));

  return <div>{defaultTo(compact(address).join(', '), ' - ')}</div>;
};

export const BillingAdminCell = ({ billingAccount }: { billingAccount: TransactionAccount }) => {
  return (
    <>
      {billingAccount?.billingAdmins?.nodes?.map((admin: BillingAdmin | undefined | null, index: number) => (
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }} key={index}>
          {defaultTo(admin?.email, '')}
        </div>
      ))}
    </>
  );
};

export const BillingAccountTable = ({ formState, selectBillingAccount, transactionAccountNumber }: Props) => {
  const [searchBarOptions] = useState<{ key: string; value: string }[]>([]);
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [rowsPerPage, setRowPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const parsedQuery = defaultTo(formState?.searchQuery, '');
  const [loadTransactionAccounts, { called, data, loading }] = useLazyQuery<
    FetchTransactionAccountSearchQuery,
    FetchTransactionAccountSearchQueryVariables
  >(queryTransactionAccount, {
    variables: { query: parsedQuery, searchOptions: defaultSearchOptions },
    displayName: 'fetchBillingAccountsForContact',
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  React.useEffect(() => {
    const parsedQuery = defaultTo(formState?.searchQuery, '');
    if (parsedQuery.trim().length > 0) {
      loadTransactionAccounts({
        variables: { query: parsedQuery, searchOptions: defaultSearchOptions },
      });
    }
  }, [formState?.searchQuery]);

  if (!called) return null;

  if (loading) return <LoadingIndicator />;

  const onPageChange = (newPage: number) => {
    setCurrentPage(newPage);
    loadTransactionAccounts({
      variables: {
        query: parsedQuery,
        searchOptions: {
          ...defaultSearchOptions,
          offset: newPage !== 1 ? newPage * rowsPerPage - rowsPerPage : 0,
          first: rowsPerPage,
        },
        filterOptions: searchBarOptions,
      },
    });
  };

  const onRowPerPageChange = (newRowPerPage: number) => {
    if (newRowPerPage === rowsPerPage) {
      return;
    }
    setCurrentPage(1);
    setRowPerPage(newRowPerPage);
    loadTransactionAccounts({
      variables: {
        query: parsedQuery,
        searchOptions: {
          ...defaultSearchOptions,
          offset: 0,
          first: newRowPerPage,
        },
        filterOptions: searchBarOptions,
      },
    });
  };

  const results = defaultTo(data?.searchForTransactionAccount?.nodes, []) as TransactionAccount[];

  const totalCount = data?.searchForTransactionAccount?.totalCount || 0;
  const showPagination = totalCount > DEFAULT_ROWS_PER_PAGE;

  const wrapInCell = (node: React.ReactElement | string) => {
    return <div className="pv-8">{node}</div>;
  };

  const getRows = () => {
    return results.map((billingAccount: TransactionAccount) => {
      return {
        key: billingAccount?.id,
        cells: [
          {
            content: wrapInCell(<BillingAccount billingAccount={billingAccount} />),
          },
          {
            content: wrapInCell(<AddressCell billingAccount={billingAccount} />),
          },
          {
            content: wrapInCell(defaultTo(billingAccount?.billToParty?.taxId, 'Not provided')),
          },
          {
            content: wrapInCell(<BillingAdminCell billingAccount={billingAccount} />),
          },
          {
            content: wrapInCell(
              billingAccount?.billToPartyPricingEligibilty ? (
                capitalize(billingAccount?.billToPartyPricingEligibilty)
              ) : (
                <div aria-label="Not applicable">-</div>
              )
            ),
          },
          {
            key: `${billingAccount?.id}-actions`,
            params: billingAccount,
            content: (
              <div className="select-acount-actions">
                <LoadingButton
                  appearance="link"
                  spacing="compact"
                  onClick={() => selectBillingAccount(billingAccount)}
                  testId="select-transaction-account"
                  isDisabled={transactionAccountNumber === billingAccount?.transactionAccountNumber}
                >
                  Select
                </LoadingButton>
              </div>
            ),
          },
        ],
      };
    });
  };

  const getHead = () => {
    const cells = [
      {
        content: 'Billing account',
        width: 23,
      },
      {
        content: 'Billing address',
        width: 23,
      },
      {
        content: 'Tax ID',
        width: 15,
      },
      {
        content: 'Billing admins',
        width: 20,
        shouldTruncate: true,
      },
      {
        content: 'Pricing',
        width: 10,
      },
      {
        content: '',
      },
    ];

    return {
      cells,
    };
  };

  return (
    <div className="contact-billing-account-container-v2">
      <DynamicResultTable
        testId="contact-billing-accounts"
        head={getHead()}
        rows={getRows()}
        emptyStateProps={emptyStateOrderProp}
        isLoading={false}
        totalCount={totalCount}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        isPaginationEnabled={showPagination}
        onRowChange={onRowPerPageChange}
      ></DynamicResultTable>
    </div>
  );
};
